import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../data/store';
import { Button, IconButton, Menu, MenuItem } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import AddIcon from '@mui/icons-material/Add';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import styled from 'styled-components';
import Table from '../common/Table';
import {
	GridColDef,
	GridSortModel,
	GridToolbarContainer,
} from '@mui/x-data-grid';
import {
	fetchCategoriesThunk,
	updateCategoryThunk,
} from '../../data/thunks/projectDataThunk';
import { ProjectDataCategory } from '../../data/models/projectData';

// Placeholder for modals (implement as needed)
const EditCategoryModal = () => null;
const AddCategoryModal = () => null;

export default function CompanyProjectData() {
	const dispatch = useDispatch<AppDispatch>();
	const { categories, loading } = useSelector(
		(state: RootState) => state.projectData
	);
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{ field: 'name', sort: 'asc' },
	]);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [menuRow, setMenuRow] = useState<ProjectDataCategory | null>(null);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [addModalOpen, setAddModalOpen] = useState(false);

	useEffect(() => {
		dispatch(fetchCategoriesThunk({}));
	}, [dispatch]);

	// Actions
	const handleEdit = (row: ProjectDataCategory) => {
		setMenuRow(row);
		setEditModalOpen(true);
		setAnchorEl(null);
	};

	const handleArchive = (row: ProjectDataCategory) => {
		// TODO: Add archive flag to category if supported by backend
		dispatch(
			updateCategoryThunk({
				...row,
				// associatedProjectConfigurations: row.associatedProjectConfigurations,
				name: row.name,
				description: row.description,
				id: row.id,
			})
		);
		setAnchorEl(null);
	};

	// Columns
	const columns: GridColDef[] = useMemo(
		() => [
			{
				field: 'name',
				headerName: 'Data Category',
				minWidth: 220,
				flex: 1,
				disableColumnMenu: true,
				sortable: true,
			},
			{
				field: 'description',
				headerName: 'Description',
				minWidth: 300,
				flex: 2,
				disableColumnMenu: true,
				sortable: false,
			},
			// {
			// 	field: 'associatedProjectConfigurations',
			// 	headerName: 'Associated Project Types',
			// 	minWidth: 200,
			// 	flex: 1,
			// 	disableColumnMenu: true,
			// 	sortable: false,
			// 	renderCell: (params: any) =>
			// 		params.value ? params.value.join(', ') : '',
			// },
			{
				field: 'numDataPoints',
				headerName: 'Number of Data Points',
				minWidth: 180,
				flex: 0.7,
				disableColumnMenu: true,
				sortable: false,
				valueGetter: (params: any) => params.row?.numDataPoints ?? 0,
			},
			{
				field: 'actions',
				headerName: '',
				minWidth: 80,
				flex: 0.3,
				disableColumnMenu: true,
				sortable: false,
				align: 'center',
				renderCell: (params: any) => (
					<IconButton
						onClick={(e) => {
							setAnchorEl(e.currentTarget);
							setMenuRow(params.row);
						}}
						size="small"
						color="primary"
					>
						<MoreHorizIcon />
					</IconButton>
				),
			},
		],
		[]
	);

	// Rows
	const rows = useMemo(() => {
		// TODO: Replace numDataPoints with actual count from definitions if available
		return categories.map((cat) => ({ ...cat, numDataPoints: 0 }));
	}, [categories]);

	// Menu
	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuRow(null);
	};

	return (
		<>
			{/* Add/Edit Modals (implement as needed) */}
			{editModalOpen && menuRow && <EditCategoryModal />}
			{addModalOpen && <AddCategoryModal />}
			{/* Actions Menu */}
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<MenuItem
					onClick={() => {
						if (menuRow) handleEdit(menuRow);
						handleMenuClose();
					}}
				>
					<EditIcon
						fontSize="small"
						color="primary"
						style={{ marginRight: 8 }}
					/>
					Edit
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (menuRow) handleArchive(menuRow);
						handleMenuClose();
					}}
				>
					<DeleteOutlineOutlinedIcon
						fontSize="small"
						color="warning"
						style={{ marginRight: 8 }}
					/>
					Archive
				</MenuItem>
			</Menu>
			{/* Table */}
			<Table
				columns={columns}
				rows={rows}
				page={0}
				pageSize={100}
				rowCount={rows.length}
				sortModel={sortModel}
				onSortModelChange={setSortModel}
				onPaginationModelChange={() => {}}
				toolbar={<Toolbar onAdd={() => setAddModalOpen(true)} />}
				loading={loading}
				boxSx={{
					height: 'calc(100vh - 215px)',
					backgroundColor: 'transparent',
				}}
			/>
		</>
	);
}

const Toolbar = ({ onAdd }: { onAdd: () => void }) => {
	return (
		<StyledGridToolbarContainer>
			<HelpText>
				Add or disable project data categories to organize your project
				data. You can associate categories with project types.
			</HelpText>
			<ButtonContainer>
				<AddToAstroButton onClick={onAdd}>
					<AddIcon style={{ marginRight: 3 }} /> Add Data Category
				</AddToAstroButton>
			</ButtonContainer>
		</StyledGridToolbarContainer>
	);
};

const ButtonContainer = styled.div`
	display: flex;
	gap: 10px;
`;

const AddToAstroButton = styled(Button)`
	text-transform: none !important;
	font-weight: normal !important;
	margin-top: 15px !important;
`;

const StyledGridToolbarContainer = styled(GridToolbarContainer)`
	display: block !important;
	padding: 0 !important;
`;

const HelpText = styled.div`
	font-size: 14px;
	color: ${(props) => props.theme.palette.text.secondary};
	margin-top: 15px;
	padding-left: 10px;
`;
